import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ChunkLoadGuard } from './components/ChunkLoadGuard';
import { RouteFallback } from './components/ui/RouteFallback';
import AppErrorFallback from './components/AppErrorFallback';
import { useStore } from './store';

// Landing pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const Hilfe = lazy(() => import('./pages/Hilfe'));
const BlogIndex = lazy(() => import('./pages/BlogIndex'));

// App pages
const Login = lazy(() => import('./pages/Login'));
const EmbedNachricht = lazy(() => import('./pages/EmbedNachricht'));
const MasterControl = lazy(() => import('./pages/MasterControl'));
const Aufgaben = lazy(() => import('./pages/Aufgaben'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const IGParasite = lazy(() => import('./pages/IGParasite'));
const ProfileCreator = lazy(() => import('./pages/ProfileCreator'));
const StrategyOS = lazy(() => import('./pages/StrategyOS'));
const BusinessOutreach = lazy(() => import('./pages/BusinessOutreach'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const PipelineEvents = lazy(() => import('./pages/PipelineEvents'));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Waehlen = lazy(() => import('./pages/Waehlen'));
const Integrations = lazy(() => import('./pages/Integrations'));
const SatelliteOS = lazy(() => import('./pages/SatelliteOS'));
const ContentVault = lazy(() => import('./pages/ContentVault'));
const TrendingMusic = lazy(() => import('./pages/TrendingMusic'));
const Reels = lazy(() => import('./pages/Reels'));
const ContactPool = lazy(() => import('./pages/ContactPool'));
const Fraud0Leads = lazy(() => import('./pages/Fraud0Leads'));
const Fraud0LeadsAnrufen = lazy(() => import('./pages/Fraud0LeadsAnrufen'));
const Fraud0LeadsHeuteDran = lazy(() => import('./pages/Fraud0LeadsHeuteDran'));
const Fraud0LeadsV2 = lazy(() => import('./pages/fraud0/Fraud0Leads'));
const LPAnalytics = lazy(() => import('./pages/LPAnalytics'));
const LeadInsightsPage = lazy(() => import('./pages/LeadInsightsPage'));
const MindDashboard = lazy(() => import('./pages/mind/MindDashboard'));
const MindWorkshop = lazy(() => import('./pages/mind/MindWorkshop'));
const MindInbox = lazy(() => import('./pages/mind/MindInbox'));
const MindChallenge = lazy(() => import('./pages/mind/MindChallenge'));
const MindRetrospective = lazy(() => import('./pages/mind/MindRetrospective'));
const MindOnboarding = lazy(() => import('./pages/mind/MindOnboarding'));
const AdminStatLibraryPage = lazy(() => import('./pages/AdminStatLibraryPage'));
const ViewConfigAdmin = lazy(() => import('./pages/ViewConfigAdmin'));
const AdminTemplateStudioPage = lazy(() => import('./pages/AdminTemplateStudioPage'));
const LinkedInPage = lazy(() => import('./pages/LinkedInPage'));
const LinkedInNeuPage = lazy(() => import('./pages/LinkedInNeuPage'));
const FirmsAdmin = lazy(() => import('./pages/FirmsAdmin'));
const Fraud0LinkedInPage = lazy(() => import('./pages/Fraud0LinkedInPage'));
const SkriptBibliothek = lazy(() => import('./pages/SkriptBibliothek'));
const JobSuche = lazy(() => import('./pages/JobSuche'));
const ExpansionAdvisor = lazy(() => import('./pages/ExpansionAdvisor'));
const CriticAudit = lazy(() => import('./pages/CriticAudit'));

function AuthGuard({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user } = useStore();
  if (!user) return <Navigate to="/app" replace />;
  if (user.role !== 'admin') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// Riegel für eine Arbeitsfläche aus der view_configs-Registry (Nr. 74 Stufe 3): wer die URL
// kennt, aber die Rolle nicht in `viewPages` hat, kommt nicht rein — anders als vorher, wo nur
// der Menüpunkt (isAdmin) versteckt war und die Route selbst offenstand (Lücke 4).
// Während `viewPagesLoaded` noch false ist, wird NICHTS gerendert und NICHT umgeleitet — sonst
// wirft der Riegel einen Berechtigten beim Neuladen der Seite raus, bevor die Antwort da ist.
function AnsichtGuard({ page, children }: { page: string; children: React.ReactNode }) {
  const { user, viewPages, viewPagesLoaded } = useStore();
  if (!user) return <Navigate to="/app" replace />;
  if (!viewPagesLoaded) return null;
  if (!viewPages.some((p) => p.page === page)) return <Navigate to="/app/fraud0-leads" replace />;
  return <>{children}</>;
}

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  // ChunkLoadGuard catches "Failed to fetch dynamically imported module" errors
  // that happen when a stale browser tab tries to lazy-load a chunk whose hash
  // changed in a newer deploy. It auto-reloads once (with sessionStorage
  // loop-protection) so users never see the React-Router default error UI.
  return (
    <ChunkLoadGuard>
      <Suspense fallback={<RouteFallback />}>
        {children}
      </Suspense>
    </ChunkLoadGuard>
  );
}

export const router = createBrowserRouter([
  // ===== LANDING (public) =====
  {
    path: '/',
    element: <SuspenseWrap><LandingPage lang="de" /></SuspenseWrap>,
  },
  {
    path: '/en',
    element: <SuspenseWrap><LandingPage lang="en" /></SuspenseWrap>,
  },
  {
    path: '/es',
    element: <SuspenseWrap><LandingPage lang="es" /></SuspenseWrap>,
  },
  {
    path: '/impressum',
    element: <SuspenseWrap><Impressum /></SuspenseWrap>,
  },
  {
    path: '/datenschutz',
    element: <SuspenseWrap><Datenschutz /></SuspenseWrap>,
  },
  {
    path: '/hilfe',
    element: <SuspenseWrap><Hilfe /></SuspenseWrap>,
  },
  {
    path: '/blog',
    element: <SuspenseWrap><BlogIndex /></SuspenseWrap>,
  },
  // i18n legal subpages
  {
    path: '/en/impressum',
    element: <SuspenseWrap><Impressum /></SuspenseWrap>,
  },
  {
    path: '/en/datenschutz',
    element: <SuspenseWrap><Datenschutz /></SuspenseWrap>,
  },
  {
    path: '/en/hilfe',
    element: <SuspenseWrap><Hilfe /></SuspenseWrap>,
  },
  {
    path: '/en/blog',
    element: <SuspenseWrap><BlogIndex /></SuspenseWrap>,
  },
  {
    path: '/es/impressum',
    element: <SuspenseWrap><Impressum /></SuspenseWrap>,
  },
  {
    path: '/es/datenschutz',
    element: <SuspenseWrap><Datenschutz /></SuspenseWrap>,
  },
  {
    path: '/es/hilfe',
    element: <SuspenseWrap><Hilfe /></SuspenseWrap>,
  },
  {
    path: '/es/blog',
    element: <SuspenseWrap><BlogIndex /></SuspenseWrap>,
  },

  // ===== APP (authenticated) =====
  {
    path: '/login',
    element: <SuspenseWrap><Login /></SuspenseWrap>,
  },
  // Einbett-Route fuer die Chrome-Extension-Seitenleiste (past-plans/wenn-man-auf-linkedin-wiggly-
  // wilkinson.md, Stufe 2). Bewusst AUSSERHALB von /app (kein AppShell, keine Sidebar) und OHNE
  // AuthGuard -- die Seite bootstrapt ihre eigene Anmeldung aus dem postMessage-Handschlag, siehe
  // Kommentar in EmbedNachricht.tsx.
  {
    path: '/embed/nachricht',
    element: <SuspenseWrap><EmbedNachricht /></SuspenseWrap>,
  },
  {
    path: '/change-password',
    element: (
      <AuthGuard>
        <SuspenseWrap><ChangePassword /></SuspenseWrap>
      </AuthGuard>
    ),
  },
  // HY2 Begleiter-Tab (past-plans/2026-09-15/14-36-brauche-eine-funktion-wo-stateful-stream.md):
  // bewusst AUSSERHALB von /app (kein AppShell, keine Sidebar — auf dem Handybildschirm nur im
  // Weg), aber MIT normalem AuthGuard, anders als /embed/nachricht: hier meldet sich derselbe
  // Nutzer auf einem zweiten Gerät an, kein Fremd-Handshake.
  {
    path: '/waehlen',
    element: (
      <AuthGuard>
        <SuspenseWrap><Waehlen /></SuspenseWrap>
      </AuthGuard>
    ),
  },
  {
    path: '/app',
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    errorElement: <AppErrorFallback />,
    children: [
      {
        index: true,
        element: <SuspenseWrap><MasterControl /></SuspenseWrap>,
      },
      {
        // Aufgaben-Board · hiess bis 12.08. "Roadmap" und lag unter /app/roadmap.
        path: 'aufgaben',
        element: <SuspenseWrap><Aufgaben /></SuspenseWrap>,
      },
      {
        // Roadmap · jetzt die Ablauf-Zeichenflaeche (bereich='roadmap'), nicht mehr das Board.
        path: 'roadmap',
        element: <SuspenseWrap><Roadmap /></SuspenseWrap>,
      },
      {
        path: 'ig-parasite',
        element: <SuspenseWrap><IGParasite /></SuspenseWrap>,
      },
      {
        path: 'ig-parasite/:tab',
        element: <SuspenseWrap><IGParasite /></SuspenseWrap>,
      },
      {
        path: 'profile-creator',
        element: <SuspenseWrap><ProfileCreator /></SuspenseWrap>,
      },
      {
        path: 'profile-creator/:tab',
        element: <SuspenseWrap><ProfileCreator /></SuspenseWrap>,
      },
      {
        path: 'strategy-os',
        element: <SuspenseWrap><StrategyOS /></SuspenseWrap>,
      },
      {
        path: 'strategy-os/:tab',
        element: <SuspenseWrap><StrategyOS /></SuspenseWrap>,
      },
      {
        path: 'satellite-os',
        element: <SuspenseWrap><SatelliteOS /></SuspenseWrap>,
      },
      {
        path: 'satellite-os/:tab',
        element: <SuspenseWrap><SatelliteOS /></SuspenseWrap>,
      },
      // Sales CRM Weg B (ADR 0205): die Seite ist weg, Altlinks/Lesezeichen bleiben heil.
      {
        path: 'sales-crm',
        element: <Navigate to="/app/fraud0-leads/anrufen" replace />,
      },
      {
        path: 'sales-crm/:tab',
        element: <Navigate to="/app/skript-bibliothek" replace />,
      },
      {
        path: 'outreach',
        element: <SuspenseWrap><BusinessOutreach /></SuspenseWrap>,
      },
      {
        path: 'fraud0-linkedin',
        element: <SuspenseWrap><Fraud0LinkedInPage /></SuspenseWrap>,
      },
      // Alte Einzel-Routen → auf die zusammengeführte Seite umleiten (Altlinks/Bookmarks bleiben heil).
      {
        path: 'linkedin-tracking',
        element: <Navigate to="/app/fraud0-linkedin" replace />,
      },
      {
        path: 'linkedin-accounts',
        element: <Navigate to="/app/fraud0-linkedin" replace />,
      },
      {
        path: 'users',
        element: (
          <AdminGuard>
            <SuspenseWrap><UserManagement /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'admin/firms',
        element: (
          <AdminGuard>
            <SuspenseWrap><FirmsAdmin /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'activity',
        element: <SuspenseWrap><ActivityLog /></SuspenseWrap>,
      },
      {
        path: 'pipeline-events',
        element: (
          <AdminGuard>
            <SuspenseWrap><PipelineEvents /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'profile',
        element: <SuspenseWrap><Profile /></SuspenseWrap>,
      },
      {
        path: 'integrations',
        element: <SuspenseWrap><Integrations /></SuspenseWrap>,
      },
      {
        path: 'vault',
        element: <SuspenseWrap><ContentVault /></SuspenseWrap>,
      },
      {
        path: 'trending-music',
        element: <SuspenseWrap><TrendingMusic /></SuspenseWrap>,
      },
      {
        path: 'reels',
        element: <SuspenseWrap><Reels /></SuspenseWrap>,
      },
      {
        // Old standalone Reel Builder removed · redirect to Content Studio
        // where the embedded VideoBuilderPanel lives. Bookmarks stay valid.
        path: 'reel-builder',
        element: <Navigate to="/strategy-os/content-studio" replace />,
      },
      {
        path: 'reel-builder/:id',
        element: <Navigate to="/strategy-os/content-studio" replace />,
      },
      {
        path: 'contact-pool',
        element: <SuspenseWrap><ContactPool /></SuspenseWrap>,
      },
      {
        path: 'fraud0-leads',
        element: <SuspenseWrap><Fraud0LeadsV2 /></SuspenseWrap>,
      },
      {
        // Klassik-/Admin-Ansicht: Tabelle, Filter, Bulk, Daten-Ops, Economics-Kalibrierung.
        // Riegel kommt aus der Registry (view_configs, Seite "fraud0_leads"), nicht aus isAdmin.
        path: 'fraud0-leads/klassik',
        element: <AnsichtGuard page="fraud0_leads"><SuspenseWrap><Fraud0Leads /></SuspenseWrap></AnsichtGuard>,
      },
      {
        // Klassik Stufe 4 "Anrufen": duenne Huelle um PhoneLeadsTable/call_table() (Sales-CRM-
        // Anrufliste, personen-genaue Zeilen), mit demselben Registry-Riegel wie Klassik.
        path: 'fraud0-leads/anrufen',
        element: <AnsichtGuard page="fraud0_leads_anrufen"><SuspenseWrap><Fraud0LeadsAnrufen /></SuspenseWrap></AnsichtGuard>,
      },
      {
        // Kalender-Umbau Schritt 4 "Heute dran": duenne Huelle um kalender_zeilen() -- die erste
        // Oberflaeche ueber den Vorrangregeln aus docs/kalender-vorrangregeln.md.
        path: 'fraud0-leads/heute-dran',
        element: <AnsichtGuard page="fraud0_leads_heute_dran"><SuspenseWrap><Fraud0LeadsHeuteDran /></SuspenseWrap></AnsichtGuard>,
      },
      {
        // Umzug PR 1 (docs/umzug-crm-anrufen.md Zeilen 32/36/37): Skript-Bibliothek als eigene Seite,
        // duenne Huelle um BlockEditor + Regal (Registry-Seite "skript_bibliothek", zuerst nur Admin).
        path: 'skript-bibliothek',
        element: <AnsichtGuard page="skript_bibliothek"><SuspenseWrap><SkriptBibliothek /></SuspenseWrap></AnsichtGuard>,
      },
      {
        path: 'skript-bibliothek/:tab',
        element: <AnsichtGuard page="skript_bibliothek"><SuspenseWrap><SkriptBibliothek /></SuspenseWrap></AnsichtGuard>,
      },
      {
        // Umzug PR 1 (Zeile 38): Job Discovery als eigene Seite (Registry-Seite "job_suche").
        path: 'job-suche',
        element: <AnsichtGuard page="job_suche"><SuspenseWrap><JobSuche /></SuspenseWrap></AnsichtGuard>,
      },
      {
        // Alias auf die neue Swipe-UI (alte Bookmarks)
        path: 'fraud0-leads-v2',
        element: <SuspenseWrap><Fraud0LeadsV2 /></SuspenseWrap>,
      },
      {
        path: 'lp-analytics',
        element: (
          <AdminGuard>
            <SuspenseWrap><LPAnalytics /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'lp-analytics/:tab',
        element: (
          <AdminGuard>
            <SuspenseWrap><LPAnalytics /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'lead-insights',
        element: (
          <AdminGuard>
            <SuspenseWrap><LeadInsightsPage /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'mind',
        element: <SuspenseWrap><MindDashboard /></SuspenseWrap>,
      },
      {
        path: 'mind/workshop',
        element: <SuspenseWrap><MindWorkshop /></SuspenseWrap>,
      },
      {
        path: 'mind/inbox',
        element: <SuspenseWrap><MindInbox /></SuspenseWrap>,
      },
      {
        path: 'mind/challenge',
        element: <SuspenseWrap><MindChallenge /></SuspenseWrap>,
      },
      {
        path: 'mind/retrospective',
        element: <SuspenseWrap><MindRetrospective /></SuspenseWrap>,
      },
      {
        path: 'mind/onboarding',
        element: <SuspenseWrap><MindOnboarding /></SuspenseWrap>,
      },
      {
        path: 'admin/stat-library',
        element: (
          <AdminGuard>
            <SuspenseWrap><AdminStatLibraryPage /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'admin/template-studio',
        element: (
          <AdminGuard>
            <SuspenseWrap><AdminTemplateStudioPage /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'admin/views',
        element: (
          <AdminGuard>
            <SuspenseWrap><ViewConfigAdmin /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'linkedin',
        element: <SuspenseWrap><LinkedInPage /></SuspenseWrap>,
      },
      {
        // Muss VOR 'linkedin/:tab' stehen, sonst frisst der Platzhalter die
        // alte Studio-Adresse. Bis 21.08. war 'studio' der Umschalter in der
        // LinkedIn-Seite; jetzt ist es eine eigene Seite.
        path: 'linkedin/studio',
        element: <Navigate to="/app/linkedin-neu" replace />,
      },
      {
        path: 'linkedin/:tab',
        element: <SuspenseWrap><LinkedInPage /></SuspenseWrap>,
      },
      {
        path: 'linkedin-neu',
        element: <SuspenseWrap><LinkedInNeuPage /></SuspenseWrap>,
      },
      {
        path: 'expansion',
        element: <SuspenseWrap><ExpansionAdvisor /></SuspenseWrap>,
      },
      {
        path: 'critic-audit',
        element: <SuspenseWrap><CriticAudit /></SuspenseWrap>,
      },
    ],
  },
]);
